import { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  useAgent,
  useAgents,
  useAgentVersions,
  useAgentAliases,
  useDeleteAgent,
  useUpdateAgent,
} from '../../hooks/useAgents';
import { usePlatformCortexLatest } from '../../hooks/usePlatformCortex';
import { useAgentSchedules } from '../../hooks/useSchedules';
import { useModelLookup } from '../../hooks/useModels';
import { useSkills } from '../../hooks/useSkills';
import { useTools } from '../../hooks/useTools';
import { useAllKnowledgeBases } from '../../hooks/useKnowledgeBases';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Badge, Button, Card, CardHeader, ConfirmModal, ControlledTabs, MarkdownContent, Switch, getStatusVariant } from '@/components/ui';
import AgentActionsMenu from './AgentActionsMenu';
import PublishAgentModal from './PublishAgentModal';
import { useToast } from '../../components/ui/Toast';
import { isPlatformEntity } from '../../types';
import { useAgentRoles } from '../../hooks/useAgentRoles';
import { useEntityPermissions } from '../../hooks/useEntityPermissions';
import { useMembers } from '../../hooks/useMembers';
import PluginManagedBanner from '../../components/plugins/PluginManagedBanner';
import BackLink from '../../components/navigation/BackLink';
import VersionHistory from '../../components/agents/VersionHistory';
import AliasManager from '../../components/agents/AliasManager';
import ScheduleList from './ScheduleList';
import { AgentSubscriptionsTab } from '../../components/subscriptions/AgentSubscriptionsTab';
import AgentCredentialsTab from '../../components/agents/AgentCredentialsTab';
import { useAgentCredentialGrants } from '../../hooks/useAgentCredentials';

export default function AgentDetail() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();

  const { data: agent, isLoading, error } = useAgent(agentId);
  const modelLookup = useModelLookup();
  const { data: versionsData } = useAgentVersions(agentId);
  const { data: aliasesData } = useAgentAliases(agentId);
  const { data: schedulesData } = useAgentSchedules(agentId);
  const { data: skillsData } = useSkills();
  const { data: toolsData } = useTools();
  const { data: agentsData } = useAgents();
  // Cross-space aggregator so we can resolve each kbId in
  // `agent.knowledgeBases` to a name regardless of which space owns it.
  const { items: allKbs } = useAllKnowledgeBases();
  // Platform agents aren't space-owned, so they have no credential grants.
  const { data: credentialGrants } = useAgentCredentialGrants(
    agent && !isPlatformEntity(agent) ? agentId : undefined,
  );

  const deleteAgent = useDeleteAgent();
  const updateAgent = useUpdateAgent();
  const { canEdit, canDelete } = useEntityPermissions();
  const { data: latestCortex } = usePlatformCortexLatest();

  // Resolve the creator's name from the space member directory. Skipped for
  // platform / cross-org agents (their owner lives in another org). Falls back
  // to null — and the field is omitted — when the user can't be resolved
  // (e.g. non-admin viewers get a 403 from the members endpoint).
  const { data: members } = useMembers(spaceId);
  const creatorName = useMemo(() => {
    if (!agent || isPlatformEntity(agent)) return null;
    const member = members?.find((m) => m.userId === agent.userId);
    if (!member) return null;
    const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
    return fullName || member.email || null;
  }, [agent, members]);

  const [activeTab, setActiveTab] = useState('configuration');
  const [publishModal, setPublishModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const versions = versionsData?.items || [];
  const aliases = aliasesData?.items || [];
  const skills = skillsData?.items || [];
  const tools = toolsData?.items || [];

  const allAgents = agentsData?.items || [];
  const rolesMap = useAgentRoles(allAgents);
  const agentSkills = skills.filter((s) => agent?.skillIds?.includes(s.skillId));
  const agentTools = tools.filter((t) => agent?.toolIds?.includes(t.toolId));
  const subAgents = allAgents.filter((a) => agent?.subAgentIds?.includes(a.agentId));
  const agentKbGrants = agent?.knowledgeBases ?? [];
  const kbsById = useMemo(
    () => new Map(allKbs.map((k) => [k.kbId, k])),
    [allKbs],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load agent</p>
        <Link to={routes.console.spaceAgents(spaceId)} className="text-primary-600 hover:text-primary-500 mt-2 inline-block">
          Back to agents
        </Link>
      </div>
    );
  }

  const cortexUpgradeAvailable =
    agent.platformCortexVersion != null &&
    latestCortex != null &&
    agent.platformCortexVersion < latestCortex.version;

  const handleCortexUpgrade = async () => {
    if (!latestCortex) return;
    try {
      await updateAgent.mutateAsync({
        agentId: agent.agentId,
        input: { platformCortexVersion: latestCortex.version },
      });
      success('Platform Cortex upgraded to v' + latestCortex.version);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to upgrade cortex');
    }
  };

  const handleToggleStatus = async () => {
    const next = agent.status === 'active' ? 'inactive' : 'active';
    try {
      await updateAgent.mutateAsync({
        agentId: agent.agentId,
        input: { status: next },
      });
      success(next === 'active' ? 'Agent activated' : 'Agent deactivated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update agent status');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAgent.mutateAsync(agent.agentId);
      success('Agent deleted successfully');
      navigate(routes.console.spaceAgents(spaceId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete agent');
    }
  };

  const tabs = [
    { id: 'configuration', label: 'Configuration' },
    { id: 'skills', label: `Skills (${agentSkills.length})` },
    { id: 'tools', label: `Tools (${agentTools.length})` },
    { id: 'subagents', label: `Sub Agents (${subAgents.length})` },
    { id: 'knowledgeBases', label: `Knowledge Bases (${agentKbGrants.length})` },
    ...(isPlatformEntity(agent)
      ? []
      : [{ id: 'credentials', label: `Credentials (${credentialGrants?.length ?? 0})` }]),
    { id: 'versions', label: `Versions (${versions.length})` },
    { id: 'aliases', label: `Aliases (${aliases.length})` },
    { id: 'subscriptions', label: `Subscriptions (${agent.eventSubscriptions?.length ?? 0})` },
    { id: 'schedules', label: `Schedules (${schedulesData?.items?.length ?? 0})` },
  ];

  return (
    <div className="space-y-6">
      <div>
        <BackLink to={routes.console.spaceAgents(spaceId)}>Agents</BackLink>
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{agent.name}</h1>
            <Badge variant={getStatusVariant(agent.status)}>{agent.status}</Badge>
            {isPlatformEntity(agent) && (
              <Badge variant="info">Platform</Badge>
            )}
            {rolesMap.get(agent.agentId)?.has('coordinator') && (
              <Badge variant="purple">Coordinator</Badge>
            )}
            {rolesMap.get(agent.agentId)?.has('sub-agent') && (
              <Badge variant="teal">Sub-agent</Badge>
            )}
            {agent.provenance?.source === 'plugin' && (
              <Badge variant="info">Plugin</Badge>
            )}
            {agent.runtime === 'claude' && (
              <Badge variant="purple">Claude Runtime</Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {agent.description || 'No description'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AgentActionsMenu
            agent={agent}
            spaceId={spaceId}
            canEdit={canEdit}
            canDelete={canDelete(agent)}
            onToggleStatus={handleToggleStatus}
            onPublish={() => setPublishModal(true)}
            onDelete={() => setDeleteModal(true)}
            isBusy={updateAgent.isPending}
          />
        </div>
      </div>
      </div>

      {agent.provenance?.source === 'plugin' && (
        <PluginManagedBanner provenance={agent.provenance} />
      )}

      {/* Cortex upgrade banner */}
      {cortexUpgradeAvailable && (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-900/30">
          <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200">
            <svg className="h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>
              Platform Cortex update available: v{agent.platformCortexVersion} → v{latestCortex!.version}
            </span>
          </div>
          <Button
            size="sm"
            onClick={handleCortexUpgrade}
            isLoading={updateAgent.isPending}
          >
            Upgrade
          </Button>
        </div>
      )}

      {/* Tabs */}
      <ControlledTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab}>
        {activeTab === 'configuration' && (
          <div className="grid gap-6">
            <Card>
              <CardHeader title="Model Configuration" />
              <dl className="grid gap-4 sm:grid-cols-3">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Model</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {modelLookup[agent.modelId]?.displayName ?? agent.modelId}
                  </dd>
                  {modelLookup[agent.modelId] && (
                    <dd className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {modelLookup[agent.modelId].capabilities
                        .filter((c) => c !== 'text')
                        .map((cap) => (
                          <Badge key={cap} variant={cap === 'vision' ? 'teal' : cap === 'reasoning' ? 'purple' : 'info'}>
                            {cap.charAt(0).toUpperCase() + cap.slice(1)}
                          </Badge>
                        ))}
                      <span className="text-xs text-muted-foreground">
                        {Math.round(modelLookup[agent.modelId].contextLimit / 1000)}K context
                      </span>
                    </dd>
                  )}
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Temperature
                  </dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {agent.temperature ?? 'Default'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Max Tokens</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {agent.maxTokens ?? 'Default'}
                  </dd>
                </div>
              </dl>
            </Card>

            <Card>
              <CardHeader title="Features" />
              <div className="flex flex-wrap gap-16">
                {[
                  { key: 'enableCodeInterpreter', label: 'Code Interpreter' },
                  { key: 'enableCaching', label: 'Prompt Caching' },
                  { key: 'enableAui', label: 'AUI Canvas' },
                  { key: 'enableAskUser', label: 'Ask User' },
                  { key: 'enableVoice', label: 'Voice' },
                ].map(({ key, label }) => {
                  // enableAskUser defaults on: enabled unless explicitly false.
                  const on =
                    key === 'enableAskUser'
                      ? agent.enableAskUser !== false
                      : !!agent[key as keyof typeof agent];
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <Switch checked={on} disabled className="pointer-events-none" />
                      <span className="text-sm text-foreground">{label}</span>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="min-w-0">
              <CardHeader title="System Prompt" />
              {agent.systemPrompt ? (
                <div className="bg-muted/60 px-6 py-4 -mx-6 -mb-6 rounded-b-lg overflow-x-auto">
                  <MarkdownContent>{agent.systemPrompt}</MarkdownContent>
                </div>
              ) : (
                <p className="text-muted-foreground">No system prompt configured.</p>
              )}
            </Card>

            <Card>
              <CardHeader title="Metadata" />
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Agent ID</dt>
                  <dd className="mt-1 text-sm text-foreground font-mono">
                    {agent.agentId}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Latest Version
                  </dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {agent.latestVersion ? `v${agent.latestVersion}` : 'None'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Created</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {new Date(agent.createdAt).toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Updated</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {new Date(agent.updatedAt).toLocaleString()}
                  </dd>
                </div>
                {creatorName && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Created by</dt>
                    <dd className="mt-1 text-sm text-foreground">{creatorName}</dd>
                  </div>
                )}
                {agent.platformCortexVersion != null && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Cortex Version</dt>
                    <dd className="mt-1 text-sm text-foreground flex items-center gap-2">
                      v{agent.platformCortexVersion}
                      {cortexUpgradeAvailable && (
                        <Badge variant="warning">update available</Badge>
                      )}
                    </dd>
                  </div>
                )}
              </dl>
            </Card>
          </div>
        )}

        {activeTab === 'skills' && (
          <Card>
            <CardHeader
              title="Assigned Skills"
              action={
                <Link to={routes.console.spaceAgentEdit(spaceId, agent.agentId)}>
                  <Button variant="secondary" size="sm">
                    Manage Skills
                  </Button>
                </Link>
              }
            />
            {agentSkills.length === 0 ? (
              <p className="text-muted-foreground">No skills assigned to this agent.</p>
            ) : (
              <div className="divide-y divide-border">
                {agentSkills.map((skill) => (
                  <div key={skill.skillId} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <Link
                          to={routes.console.spaceSkill(spaceId, skill.skillId)}
                          className="font-medium text-foreground hover:text-primary-600"
                        >
                          {skill.name}
                        </Link>
                        <p className="text-sm text-muted-foreground">{skill.description}</p>
                      </div>
                      <Badge variant={getStatusVariant(skill.status)}>
                        {skill.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {activeTab === 'tools' && (
          <Card>
            <CardHeader
              title="Assigned Tools"
              action={
                <Link to={routes.console.spaceAgentEdit(spaceId, agent.agentId)}>
                  <Button variant="secondary" size="sm">
                    Manage Tools
                  </Button>
                </Link>
              }
            />
            {agentTools.length === 0 ? (
              <p className="text-muted-foreground">No tools assigned to this agent.</p>
            ) : (
              <div className="divide-y divide-border">
                {agentTools.map((tool) => (
                  <div key={tool.toolId} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <Link
                          to={routes.console.spaceTool(spaceId, tool.toolId)}
                          className="font-medium text-foreground hover:text-primary-600"
                        >
                          {tool.name}
                        </Link>
                        <p className="text-sm text-muted-foreground">{tool.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="info">{tool.type}</Badge>
                        <Badge variant={getStatusVariant(tool.status)}>
                          {tool.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {activeTab === 'knowledgeBases' && (
          <Card>
            <CardHeader
              title="Knowledge Bases"
              action={
                <Link to={routes.console.spaceAgentEdit(spaceId, agent.agentId)}>
                  <Button variant="secondary" size="sm">
                    Manage Knowledge Bases
                  </Button>
                </Link>
              }
            />
            {agentKbGrants.length === 0 ? (
              <p className="text-muted-foreground">No knowledge bases attached.</p>
            ) : (
              <div className="divide-y divide-border">
                {agentKbGrants.map((grant) => {
                  const kb = kbsById.get(grant.kbId);
                  return (
                    <div key={grant.kbId} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Link
                              to={routes.console.spaceKb(kb?.spaceId ?? spaceId, grant.kbId)}
                              className="truncate font-medium text-foreground hover:text-primary-600"
                            >
                              {kb?.name ?? grant.kbId}
                            </Link>
                            {kb?.scope === 'org' && (
                              <Badge variant="purple">org</Badge>
                            )}
                            {kb?.spaceName && kb.scope === 'space' && (
                              <span className="text-xs text-muted-foreground">
                                · {kb.spaceName}
                              </span>
                            )}
                          </div>
                          {kb?.description && (
                            <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                              {kb.description}
                            </p>
                          )}
                        </div>
                        <Badge variant={grant.write ? 'info' : 'gray'}>
                          {grant.write ? 'Read + Write' : 'Read'}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}

        {activeTab === 'credentials' && !isPlatformEntity(agent) && (
          <AgentCredentialsTab agentId={agent.agentId} canEdit={canEdit} />
        )}

        {activeTab === 'versions' && (
          <VersionHistory
            agentId={agent.agentId}
            versions={versions}
            currentVersion={agent.latestVersion}
          />
        )}

        {activeTab === 'aliases' && (
          <AliasManager
            agentId={agent.agentId}
            aliases={aliases}
            versions={versions}
          />
        )}

        {activeTab === 'subscriptions' && (
          <Card>
            <CardHeader title="Event Subscriptions" />
            <AgentSubscriptionsTab agentId={agent.agentId} />
          </Card>
        )}

        {activeTab === 'schedules' && (
          <ScheduleList agentId={agent.agentId} />
        )}

        {activeTab === 'subagents' && (
          <Card>
            <CardHeader
              title="Sub Agents"
              action={
                !isPlatformEntity(agent) ? (
                  <Link to={routes.console.spaceAgentEdit(spaceId, agent.agentId)}>
                    <Button variant="secondary" size="sm">
                      Manage Sub Agents
                    </Button>
                  </Link>
                ) : undefined
              }
            />
            {subAgents.length === 0 ? (
              <p className="text-muted-foreground">No sub agents assigned to this agent.</p>
            ) : (
              <div className="divide-y divide-border">
                {subAgents.map((sub) => (
                  <div key={sub.agentId} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <Link
                          to={routes.console.spaceAgent(spaceId, sub.agentId)}
                          className="font-medium text-foreground hover:text-primary-600"
                        >
                          {sub.name}
                        </Link>
                        <p className="text-sm text-muted-foreground">{sub.description}</p>
                      </div>
                      <Badge variant={getStatusVariant(sub.status)}>
                        {sub.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </ControlledTabs>

      {/* Publish modal */}
      <PublishAgentModal
        agent={agent}
        isOpen={publishModal}
        onClose={() => setPublishModal(false)}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Agent"
        message={`Are you sure you want to delete "${agent.name}"? This will also delete all versions and aliases. This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteAgent.isPending}
      />
    </div>
  );
}
